import type { FC } from 'react';

import Pagination from '@/common-components/Pagination';
import CampusCard from '@/features/campuses/components/CampusCard';
import CampusFormModal from '@/features/campuses/components/CampusFormModal';
import DeleteCampusModal from '@/features/campuses/components/DeleteCampusModal';
import { useCampusesPage } from '@/features/campuses/hooks/useCampusesPage.hook';
import type { CampusStatus } from '@/types/campus.types';

const STATUS_FILTERS: { label: string; value: 'all' | CampusStatus }[] = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Paused', value: 'paused' },
];

const CampusesPage: FC = () => {
  const vm = useCampusesPage();

  return (
    <>
      <div className="flex flex-col gap-6">
        {/* Header */}
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-[22px] font-extrabold tracking-tight text-[var(--color-ink)]">
              Campus Management
            </h1>
            <p className="mt-1 text-[13px] text-[var(--color-ink-muted)]">
              {vm.total.toLocaleString()} campus{vm.total === 1 ? '' : 'es'}
            </p>
          </div>
          <button
            type="button"
            onClick={vm.onOpenAdd}
            className="inline-flex items-center gap-2 self-start rounded-lg bg-brand-500 px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-brand-600"
          >
            + Add Campus
          </button>
        </header>

        {/* Toolbar */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {/* Status filter chips */}
          <div className="flex gap-2">
            {STATUS_FILTERS.map(({ label, value }) => (
              <button
                key={value}
                type="button"
                onClick={() => vm.onChangeStatus(value)}
                className={`rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition-colors ${
                  vm.statusFilter === value
                    ? 'border-brand-500 bg-brand-500 text-white'
                    : 'border-[var(--color-divider)] bg-white text-[var(--color-ink-muted)] hover:border-brand-300 hover:text-brand-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search campuses…"
            value={vm.search}
            onChange={(e) => vm.onChangeSearch(e.target.value)}
            className="w-full rounded-lg border border-[var(--color-divider)] bg-white px-3 py-2 text-[13px] text-[var(--color-ink)] outline-none placeholder:text-[var(--color-ink-muted)] focus:border-brand-400 focus:ring-2 focus:ring-brand-100 sm:w-60"
          />
        </div>

        {/* Grid / states */}
        {vm.isLoading ? (
          <div className="flex min-h-64 items-center justify-center text-[13px] text-[var(--color-ink-muted)]">
            Loading campuses…
          </div>
        ) : vm.isError ? (
          <div className="flex min-h-64 flex-col items-center justify-center gap-2 p-6 text-center">
            <p className="text-sm font-semibold text-[var(--color-danger-ink)]">
              Couldn't load campuses
            </p>
            <p className="text-xs text-[var(--color-ink-muted)]">
              {vm.errorMessage ?? 'Please try again.'}
            </p>
          </div>
        ) : vm.campuses.length === 0 ? (
          <div className="flex min-h-64 flex-col items-center justify-center gap-2 p-6 text-center">
            <span className="text-3xl">🏫</span>
            <p className="text-sm font-semibold text-[var(--color-ink)]">No campuses found</p>
            <p className="text-xs text-[var(--color-ink-muted)]">
              {vm.search || vm.statusFilter !== 'all'
                ? 'Try clearing your search or filter.'
                : 'Add your first campus to get started.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {vm.campuses.map((campus) => (
              <CampusCard
                key={campus.id}
                campus={campus}
                togglingStatusId={vm.togglingStatusId}
                onEdit={vm.onOpenEdit}
                onDelete={vm.onOpenDelete}
                onToggleStatus={vm.onToggleStatus}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {vm.totalPages > 1 ? (
          <div className="flex justify-end">
            <Pagination page={vm.page} totalPages={vm.totalPages} onChange={vm.onChangePage} />
          </div>
        ) : null}
      </div>

      {/* Form modal */}
      {vm.modalState.kind === 'add' || vm.modalState.kind === 'edit' ? (
        <CampusFormModal
          campus={vm.modalState.kind === 'edit' ? vm.modalState.campus : null}
          formValues={vm.formValues}
          isApplyingBannerToAll={vm.isApplyingBannerToAll}
          applyAllResult={vm.applyAllResult}
          onApplyBannerToAll={() => vm.onApplyBannerToAll(vm.formValues.banner)}
          domainInput={vm.domainInput}
          deptInput={vm.deptInput}
          formError={vm.formError}
          isSaving={vm.isSaving}
          isUploadingBanner={vm.isUploadingBanner}
          bannerUploadError={vm.bannerUploadError}
          onChangeField={vm.onChangeFormField}
          onChangeDomainInput={vm.onChangeDomainInput}
          onAddDomain={vm.onAddDomain}
          onRemoveDomain={vm.onRemoveDomain}
          onChangeDeptInput={vm.onChangeDeptInput}
          onAddDept={vm.onAddDept}
          onRemoveDept={vm.onRemoveDept}
          onChangeBannerField={vm.onChangeBannerField}
          onUploadBannerImage={vm.onUploadBannerImage}
          onRemoveBannerImage={vm.onRemoveBannerImage}
          onSubmit={vm.onSubmitForm}
          onClose={vm.onCloseModal}
        />
      ) : null}

      {/* Delete confirmation modal */}
      {vm.modalState.kind === 'delete' ? (
        <DeleteCampusModal
          campus={vm.modalState.campus}
          isDeleting={vm.isDeleting}
          onConfirm={vm.onConfirmDelete}
          onClose={vm.onCloseModal}
        />
      ) : null}
    </>
  );
};

export default CampusesPage;
